import { PeliasLayer, PeliasFeature } from "./PeliasFeature"

export interface FieldConfiguration {
    layers: PeliasLayer[]
    label: string
    filter: (feature: Partial<PeliasFeature>) => boolean
    getValue: (feature: Partial<PeliasFeature> | undefined) => string
    maxLength: number
}

export type FieldName = "region" | "city" | "postcode" | "street"

// data sourced from geonames does not have bbox
// so we must filter them out on the region / city layer.
export const FieldConfiguration = {
    get: (countryCode: string | undefined, fieldName: FieldName): FieldConfiguration => {
        countryCode = countryCode || '';

        const configLayers = layers[fieldName][countryCode] || layers[fieldName].default;
        const config: FieldConfiguration = {
            maxLength: maxLengths[fieldName],
            label: labels[fieldName][countryCode] || labels[fieldName].default,
            filter: filters[fieldName],
            layers: configLayers,
            getValue: getValue[fieldName][countryCode] || getValue[fieldName].default,
        };

        return config;
    }
}

const hasBBox = (feature: Partial<PeliasFeature>) => !!feature.bbox;
const filters: { [key in FieldName]: FieldConfiguration["filter"] } = {
    region: hasBBox,
    city: () => true,
    postcode: () => true,
    street: () => true
}

const maxLengths: Record<FieldName, FieldConfiguration["maxLength"]> = {
    region: 100,
    city: 60,
    postcode: 10,
    street: 255,
}

const getRegion_a = (feature: Partial<PeliasFeature> | undefined) => feature?.properties?.region_a || feature?.properties?.region || ''
const getValue: { [keyo in FieldName]: Record<string, FieldConfiguration["getValue"]> } = {
    region: {
        AU: getRegion_a,
        US: getRegion_a,
        GB: (feature: Partial<PeliasFeature> | undefined) => PeliasFeature.joinProperties(feature, layers.region.default),
        NZ: (feature: Partial<PeliasFeature> | undefined) => {
            var result = PeliasFeature.joinProperties(feature, layers.region.default)
            return result.endsWith(" Region") ? result.substring(0, result.length - 7) : result;
        },
        default: (feature: Partial<PeliasFeature> | undefined) => PeliasFeature.joinProperties(feature, layers.region.default)
    },
    city: {
        AU: (feature: Partial<PeliasFeature> | undefined) => {
            return [feature?.properties?.county, feature?.properties?.macrocounty].reduce((prev: string, prop) => {
                var append = prop
                    ?.split(" ")
                    .filter(x => ["Urban", "Regional", "Area", "-"].indexOf(x) === -1 && prev?.indexOf(x) == -1)
                    .join(" ");
                
                return append ? `${prev}, ${append}` : prev;
            }, PeliasFeature.joinProperties(feature, layers.city.default))?.trimEnd();
        },
        GB: (feature: Partial<PeliasFeature> | undefined) => PeliasFeature.joinProperties(feature, layers.city.default),
        default: (feature: Partial<PeliasFeature> | undefined) => PeliasFeature.joinProperties(feature, layers.city.default)
    },
    postcode: {
        default: (feature: Partial<PeliasFeature> | undefined) => feature?.properties?.postalcode || ''
    },
    street: {
        default: (feature: Partial<PeliasFeature> | undefined) => feature?.properties?.name || ''
    }
}

const layers: Record<FieldName, Record<string, FieldConfiguration["layers"]>> = {
    region: {
        default: [
            "region",
            "macroregion"
        ]
    },
    city: {
        default: [
            "neighbourhood",
            "borough",
            "locality",
            "county"
        ]
    },
    postcode: {
        default: [
            "postalcode"
        ]
    },
    street: {
        default: [
            "venue",
            "street",
            "address",
            "intersection"
        ]
    }
}

const labels: Record<FieldName, Record<string, FieldConfiguration["label"]>> = {
    region: {
        AU: 'State',
        CA: 'Province',
        GB: 'County / Region',
        NZ: 'Region',
        US: 'State',
        ZA: 'Province',
        default: 'State / Province / Region'
    },
    city: {
        AU: 'City / Town / Suburb',
        default: 'City / Town / District'
    },
    postcode: {
        CA: 'Zip code',
        US: 'Zip code',
        default: 'Post code'
    },
    street: {
        default: "Street / Venue"
    }
}